import React, { useState, useRef, useEffect } from 'react';
import { User } from '../interfaces/User';
import './mention-textarea.scss';

interface MentionTextareaProps {
    value: string;
    onChange: (value: string, mentionedUsers: User[]) => void;
    placeholder?: string;
    rows?: number;
    users: User[];
    textAreaRef?: React.RefObject<HTMLTextAreaElement>;
}

const MentionTextarea: React.FC<MentionTextareaProps> = ({ value, onChange, placeholder, rows = 3, users, textAreaRef }) => {
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState<User[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [mentionStart, setMentionStart] = useState<number | null>(null);
    const [mentionedUsers, setMentionedUsers] = useState<User[]>([]);
    const internalTextAreaRef = useRef<HTMLTextAreaElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);
    const activeTextAreaRef = textAreaRef || internalTextAreaRef;

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        const cursorPosition = e.target.selectionStart;

        // Filter out mentioned users whose names are no longer in the text
        const stillMentionedUsers = mentionedUsers.filter((user) => {
            const mentionText = `@${user.fullName}`;
            return newValue.includes(mentionText);
        });

        if (stillMentionedUsers.length !== mentionedUsers.length) {
            setMentionedUsers(stillMentionedUsers);
        }

        // Check if user typed '@'
        const textBeforeCursor = newValue.substring(0, cursorPosition);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
            // Check if there's no space after @ (still typing the mention)
            if (!textAfterAt.includes(' ') && textAfterAt.length >= 0) {
                const query = textAfterAt.toLowerCase();
                // Filter only active users
                const filtered = users.filter(
                    (u) =>
                        u.isActive &&
                        (u.fullName?.toLowerCase().includes(query) ||
                            u.firstName?.toLowerCase().includes(query) ||
                            u.lastName?.toLowerCase().includes(query)),
                );
                setSuggestions(filtered);
                setShowSuggestions(filtered.length > 0);
                setMentionStart(lastAtIndex);
                setSelectedIndex(0);
            } else {
                setShowSuggestions(false);
            }
        } else {
            setShowSuggestions(false);
        }

        onChange(newValue, stillMentionedUsers);
    };

    const selectUser = (user: User) => {
        if (mentionStart === null || !activeTextAreaRef.current) return;

        const cursorPosition = activeTextAreaRef.current.selectionStart;
        const textBeforeMention = value.substring(0, mentionStart);
        const textAfterCursor = value.substring(cursorPosition);
        const newValue = `${textBeforeMention}@${user.fullName} ${textAfterCursor}`;

        // Add user to mentioned users if not already there
        const newMentionedUsers = mentionedUsers.find((u) => u._id === user._id)
            ? mentionedUsers
            : [...mentionedUsers, user];

        setMentionedUsers(newMentionedUsers);
        onChange(newValue, newMentionedUsers);
        setShowSuggestions(false);
        setMentionStart(null);

        // Set cursor position after the mention
        setTimeout(() => {
            const newCursorPos = mentionStart + user.fullName!.length + 2; // +2 for @ and space
            activeTextAreaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
            activeTextAreaRef.current?.focus();
        }, 0);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (!showSuggestions) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
                break;
            case 'Enter':
                if (showSuggestions && suggestions[selectedIndex]) {
                    e.preventDefault();
                    selectUser(suggestions[selectedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setShowSuggestions(false);
                break;
        }
    };

    // Scroll selected item into view
    useEffect(() => {
        if (suggestionsRef.current) {
            const selectedElement = suggestionsRef.current.children[selectedIndex] as HTMLElement;
            if (selectedElement) {
                selectedElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [selectedIndex]);

    return (
        <div className="mention-textarea-wrapper">
            <textarea
                ref={activeTextAreaRef}
                rows={rows}
                className="form-control border-0 resize-none"
                placeholder={placeholder}
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
            />
            {showSuggestions && (
                <div className="mention-suggestions" ref={suggestionsRef}>
                    {suggestions.map((user, index) => (
                        <div
                            key={user._id}
                            className={`mention-suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                            onClick={() => selectUser(user)}
                            onMouseEnter={() => setSelectedIndex(index)}
                        >
                            {user.fullName}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MentionTextarea;
